import { useMemo } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../../shared/components/formik/FormikSelectField'
import FormikEditorField from '../../../../shared/components/formik/FormikEditorField'
import FormikFileField from '../../../../shared/components/formik/FormikFileField'

const CONTENT_TYPE_TO_LANGUAGE = {
  'text/plain': 'plaintext',
  'application/json': 'json',
  'application/x-yaml': 'yaml',
  'text/yaml': 'yaml',
  'application/xml': 'xml',
  'text/html': 'html',
  'text/css': 'css',
  'application/javascript': 'javascript',
  'text/markdown': 'markdown',
  'application/x-sh': 'shell',
  'text/x-python': 'python',
  'application/x-ruby': 'ruby'
}

const ProjectFileForm = ({ file, mode, onSubmit, onCancel }) => {
  const intl = useIntl()
  const isEditing = Boolean(file)
  const isTextMode = mode === 'text'

  const contentTypeOptions = useMemo(() => [
    { value: 'text/plain', label: intl.formatMessage({ id: 'projectFiles.contentTypes.textPlain' }) },
    { value: 'application/json', label: intl.formatMessage({ id: 'projectFiles.contentTypes.json' }) },
    { value: 'application/x-yaml', label: intl.formatMessage({ id: 'projectFiles.contentTypes.yaml' }) },
    { value: 'application/xml', label: intl.formatMessage({ id: 'projectFiles.contentTypes.xml' }) },
    { value: 'text/html', label: intl.formatMessage({ id: 'projectFiles.contentTypes.html' }) },
    { value: 'text/css', label: intl.formatMessage({ id: 'projectFiles.contentTypes.css' }) },
    { value: 'application/javascript', label: intl.formatMessage({ id: 'projectFiles.contentTypes.javascript' }) },
    { value: 'text/markdown', label: intl.formatMessage({ id: 'projectFiles.contentTypes.markdown' }) },
    { value: 'application/x-sh', label: intl.formatMessage({ id: 'projectFiles.contentTypes.shell' }) }
  ], [intl])

  const textValidationSchema = Yup.object({
    fileName: Yup.string().required(intl.formatMessage({ id: 'validation.required' })),
    fileContentType: Yup.string().required(intl.formatMessage({ id: 'validation.required' })),
    textContent: Yup.string()
  })

  const binaryValidationSchema = Yup.object({
    binaryFile: Yup.mixed().required(intl.formatMessage({ id: 'validation.required' }))
  })

  const textFormik = useFormik({
    initialValues: {
      fileName: file?.fileName || '',
      fileContentType: file?.fileContentType || 'text/plain',
      textContent: file?.textContent || ''
    },
    validationSchema: textValidationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      onSubmit(values)
        .then(() => {
          // Success handled by parent
        })
        .catch((err) => {
          setStatus(err.message || intl.formatMessage({ id: 'projectFiles.form.error' }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  const binaryFormik = useFormik({
    initialValues: {
      binaryFile: null
    },
    validationSchema: binaryValidationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      onSubmit(values)
        .then(() => {
          // Success handled by parent
        })
        .catch((err) => {
          setStatus(err.message || intl.formatMessage({ id: 'projectFiles.form.error' }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  const formik = isTextMode ? textFormik : binaryFormik
  const editorLanguage = CONTENT_TYPE_TO_LANGUAGE[textFormik.values.fileContentType] || 'plaintext'

  if (isTextMode) {
    return (
      <Box component="form" onSubmit={formik.handleSubmit}>
        <FormikTextField
          formik={formik}
          name="fileName"
          label={<FormattedMessage id="projectFiles.form.fileNameLabel" />}
          autoFocus
        />

        <Box sx={{ mt: 2 }}>
          <FormikSelectField
            formik={formik}
            name="fileContentType"
            label={<FormattedMessage id="projectFiles.form.fileContentTypeLabel" />}
            options={contentTypeOptions}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <FormattedMessage id="projectFiles.form.textContentLabel" />
          </Typography>
          <FormikEditorField
            formik={formik}
            name="textContent"
            language={editorLanguage}
            height="40vh"
          />
        </Box>

        {formik.status && (
          <Typography color="error" sx={{ mt: 2 }}>
            {formik.status}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={formik.isSubmitting}
          >
            <FormattedMessage id="app.actions.cancel.label" />
          </Button>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={formik.isSubmitting}
          >
            <FormattedMessage
              id={isEditing ? 'projectFiles.edit.submitButton' : 'projectFiles.create.submitButton'}
            />
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikFileField
        formik={formik}
        name="binaryFile"
        label={intl.formatMessage({ id: 'projectFiles.form.binaryFileLabel' })}
      />

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="projectFiles.createBinary.submitButton" />
        </Button>
      </Box>
    </Box>
  )
}

export default ProjectFileForm
